import {
  Combobox,
  ComboboxItem,
  Loader,
  MultiSelectProps,
  Pill,
  PillsInput,
  ScrollArea,
  useCombobox,
} from "@mantine/core"
import { useTranslation } from "react-i18next"

export interface DefaultSelectProps
  extends Omit<MultiSelectProps, "renderOption"> {
  options: ComboboxItem[]
  loading?: boolean
  multiple?: boolean
  onSelectionChange?: (selectedValues: string[]) => void
  onSearchChange?: (searchValue: string) => void
  renderOption?: (item: ComboboxItem) => JSX.Element
  onScrolledToBottom?: () => void
}

export function DefaultSelect({
  options,
  multiple,
  loading,
  searchValue,
  onSelectionChange,
  onScrolledToBottom,
  onSearchChange,
  renderOption,
  label,
  value,
  placeholder,
  ...props
}: DefaultSelectProps) {
  const [internalSearchValue, setinternalSearchValue] = React.useState(
    searchValue ?? ""
  )

  React.useEffect(
    () => onSearchChange && onSearchChange(internalSearchValue),
    [internalSearchValue]
  )

  React.useEffect(() => {
    setinternalSearchValue(searchValue ?? "")
  }, [searchValue])

  const [selectedValues, setSelectedValues] = React.useState<string[]>(
    value ?? []
  )

  React.useEffect(() => {
    onSelectionChange && onSelectionChange(selectedValues)
  }, [selectedValues])

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  })

  const handleValueSelect = (val: string) => {
    setSelectedValues((current) =>
      multiple
        ? current.includes(val)
          ? current.filter((v) => v !== val)
          : [...current, val]
        : [val]
    )
    if (!multiple) {
      combobox.closeDropdown()
      setinternalSearchValue("")
    }
  }

  const handleValueRemove = (val: string) =>
    setSelectedValues((current) => current.filter((v) => v !== val))

  const viewportRef = React.useRef<HTMLDivElement>(null)

  const { t } = useTranslation("app_server")

  return (
    <Combobox onOptionSubmit={handleValueSelect} store={combobox}>
      <Combobox.Target>
        <PillsInput
          disabled={props.disabled}
          label={label}
          onClick={() => combobox.openDropdown()}
        >
          <Pill.Group>
            {selectedValues.map((selectedValue) => {
              const option = options.find(
                (option) => option.value === selectedValue
              )
              if (option?.value) {
                return (
                  <Pill
                    key={option.value}
                    withRemoveButton
                    onRemove={() => handleValueRemove(option.value)}
                  >
                    {option.label}
                  </Pill>
                )
              } else {
                // happens if data is not loaded/paginated
                return (
                  <Pill
                    key={selectedValue}
                    withRemoveButton
                    onRemove={() => handleValueRemove(selectedValue)}
                  >
                    {selectedValue}
                  </Pill>
                )
              }
            })}

            {(multiple || selectedValues.length === 0) && (
              <Combobox.EventsTarget>
                <PillsInput.Field
                  onFocus={() => combobox.openDropdown()}
                  onBlur={() => combobox.closeDropdown()}
                  value={internalSearchValue}
                  placeholder={placeholder || t("Search")}
                  onChange={(event) => {
                    combobox.updateSelectedOptionIndex()
                    setinternalSearchValue(event.currentTarget.value)
                  }}
                  onKeyDown={(event) => {
                    if (
                      event.key === "Backspace" &&
                      internalSearchValue.length === 0 &&
                      selectedValues.length > 0
                    ) {
                      event.preventDefault()
                      handleValueRemove(
                        selectedValues[selectedValues.length - 1]
                      )
                    }
                  }}
                />
              </Combobox.EventsTarget>
            )}
          </Pill.Group>
        </PillsInput>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          <ScrollArea
            viewportRef={viewportRef}
            style={{ height: 300 }}
            onScrollPositionChange={() => {
              const scrollArea = viewportRef.current
              if (
                scrollArea &&
                scrollArea.scrollHeight -
                  scrollArea.scrollTop -
                  scrollArea.clientHeight <
                  10 &&
                onScrolledToBottom
              ) {
                onScrolledToBottom()
              }
            }}
          >
            {options.length === 0 ? (
              <Combobox.Empty>Nothing found</Combobox.Empty>
            ) : (
              options.map((option) => {
                return renderOption ? (
                  renderOption(option)
                ) : (
                  <Combobox.Option {...option} key={option.value}>
                    {option.label}
                  </Combobox.Option>
                )
              })
            )}
            {loading && (
              <div style={{ padding: 8 }}>
                <Loader size="xs" />
              </div>
            )}
          </ScrollArea>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}
